import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const OperationDetailDialog = ({ open, onClose, operation, calendarId, currency, onOperationChanged }) => {
  const [formData, setFormData] = useState({
    date: '',
    type: 'debit',
    amount: '',
    category: '',
    payment_method: '',
    account: '',
    comment: ''
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && operation) {
      setFormData({
        date: operation.date,
        type: operation.type,
        amount: operation.amount.toString(),
        category: operation.category,
        payment_method: operation.payment_method || '',
        account: operation.account || '',
        comment: operation.comment || ''
      });
      loadCategories();
    }
  }, [open, operation]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(
        `${API}/categories?calendar_id=${calendarId}`,
        { withCredentials: true }
      );
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method || null,
        account: formData.account || null,
        comment: formData.comment || null
      };

      await axios.put(
        `${API}/operations/${operation.id}`,
        data,
        { withCredentials: true }
      );

      toast.success('✅ Opération modifiée');
      onOperationChanged();
      onClose();
    } catch (error) {
      console.error('Error updating operation:', error);
      toast.error('Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  if (!operation) return null;

  const filteredCategories = categories.filter(cat => cat.type === formData.type);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="operation-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-blue-900">Modifier l'opération</DialogTitle>
          <DialogDescription>
            Ajuste les informations de cette transaction
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              data-testid="edit-date-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Type</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value, category: '' })}
            >
              <SelectTrigger data-testid="edit-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="credit">Crédit</SelectItem>
                <SelectItem value="debit">Débit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Montant ({currency})</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
              data-testid="edit-amount-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Catégorie</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
              required
            >
              <SelectTrigger data-testid="edit-category-select">
                <SelectValue placeholder="Sélectionner" />
              </SelectTrigger>
              <SelectContent>
                {filteredCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment_method">Moyen de paiement</Label>
            <Input
              id="payment_method"
              placeholder="CB, Espèces..."
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              data-testid="edit-payment-method-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="account">Compte</Label>
            <Input
              id="account"
              placeholder="Compte courant..."
              value={formData.account}
              onChange={(e) => setFormData({ ...formData, account: e.target.value })}
              data-testid="edit-account-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="comment">Commentaire</Label>
            <Textarea
              id="comment"
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              rows={3}
              data-testid="edit-comment-input"
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={loading} 
              className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
              data-testid="save-edit-button"
            >
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OperationDetailDialog;